import type { OptimizationBlockState } from '../../../lib/optimization/types'
import { sanitizeOptimizationUserMessage } from '../../../lib/optimization/optimizationUserMessages'
import { useDialogKeyboardShortcuts } from '../../../hooks/useDialogKeyboardShortcuts'

interface OptimizationFailureDialogProps {
  block: OptimizationBlockState | null
  onClose: () => void
}

function formatFeedback(block: OptimizationBlockState): string {
  const fb = block.agent_feedback
  if (!fb) {
    return block.status === 'failed'
      ? 'Sub-agent failed without structured feedback.'
      : 'No feedback recorded.'
  }

  const parts: string[] = []
  if (fb.verdict) parts.push(`Verdict: ${fb.verdict}`)
  if (fb.failure_reason) {
    parts.push(`Reason: ${sanitizeOptimizationUserMessage(fb.failure_reason)}`)
  }
  const recs = [...(fb.contact_recommendations || []), ...(fb.recommendations || [])].filter(
    (item, index, arr) => typeof item === 'string' && arr.indexOf(item) === index
  )
  if (recs.length) {
    parts.push('Contact recommendations:')
    for (const rec of recs) {
      parts.push(`- ${rec}`)
    }
  }
  if (fb.changes?.length) {
    parts.push('Changes:')
    for (const ch of fb.changes) {
      parts.push(typeof ch === 'string' ? ch : JSON.stringify(ch, null, 2))
    }
  }
  if (fb.section_review) {
    parts.push('Section review:')
    parts.push(JSON.stringify(fb.section_review, null, 2))
  }
  if (fb.apply_patch) {
    parts.push('Apply patch:')
    parts.push(JSON.stringify(fb.apply_patch, null, 2))
  }
  if (fb.raw) {
    parts.push('Raw agent output:')
    parts.push(JSON.stringify(fb.raw, null, 2))
  }
  return parts.join('\n\n') || 'Empty feedback payload.'
}

export default function OptimizationFailureDialog({
  block,
  onClose,
}: OptimizationFailureDialogProps) {
  const isOpen = block !== null

  useDialogKeyboardShortcuts({
    isOpen,
    onCancel: onClose,
    onConfirm: onClose,
  })

  if (!block) return null

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div
        className="fixed inset-0 bg-overlay transition-opacity"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="opt-fail-title"
          className="relative max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-lg bg-surface-default shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-border-default px-5 py-4">
            <h2 id="opt-fail-title" className="text-lg font-semibold text-text-primary">
              {block.label} — partial apply
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              Agent feedback and why the full block was not applied.
            </p>
          </div>
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words px-5 py-4 text-xs text-text-primary bg-surface-muted font-mono">
            {formatFeedback(block)}
          </pre>
          <div className="flex justify-end border-t border-border-default px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-white hover:bg-brand-primary-hover"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
